/**
 * Reads the time out of a freeform trip phrase, leaving the rest as the place.
 *
 * The home-page field invites answers like "Japan in October", and the inquiry
 * form takes that phrase whole: one field, filled in with what they typed. The
 * split is used in two places behind that. The dream report tallies the "when"
 * halves, which is the one readout that says what is worth selling this month,
 * and the planning profile offers the "when" half back as the answer to its own
 * timing question, so somebody who already said October is not asked twice.
 *
 * This is deliberately conservative. It fires only on time expressions people
 * actually type about travel and leaves everything else alone, because the cost
 * of a miss (no timing found, the phrase stays whole) is far lower than the cost
 * of a bad split. Nothing it returns is stored on the lead: the phrase is kept
 * as typed, and a wrong guess costs one edit on an optional question.
 */
const MONTHS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
    'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sept', 'sep', 'oct', 'nov', 'dec',
];
const SEASONS = ['spring', 'summer', 'fall', 'autumn', 'winter'];
const HOLIDAYS = [
    'christmas', 'new years', "new year's", 'new year', 'thanksgiving',
    'easter', 'hanukkah', 'diwali', 'ramadan',
];
/**
 * Words that are a month or a season only when something marks them as one.
 * "May" and "march" and "fall" are ordinary English words, so a bare match is
 * how you end up reading "I may go" as a departure date.
 */
const AMBIGUOUS = new Set(['may', 'march', 'fall', 'sept']);
const PREPOSITION = '(?:in|during|around|over|for|about|sometime in|some time in)';
const QUALIFIER = '(?:next|this|late|early|mid|the)';
const alt = (words) => words.join('|');
/**
 * Ordered by how much context each pattern carries. The first match wins, so
 * "in early March 2027" is read as one phrase rather than three fragments.
 */
const WHEN_PATTERNS = [
    // A range first, so "January or February" stays one answer instead of being
    // torn in half with the second month stranded in the destination.
    new RegExp(`\\b(?:${PREPOSITION}\\s+)?(?:${QUALIFIER}\\s+)?(?:${alt(MONTHS)}|${alt(SEASONS)})\\s*(?:or|to|through|until|-|–)\\s*(?:${alt(MONTHS)}|${alt(SEASONS)})(?:\\s+(?:of\\s+)?20\\d\\d)?\\b`, 'i'),
    // "in October 2027", "for early spring 2028", "around Christmas"
    new RegExp(`\\b${PREPOSITION}\\s+(?:${QUALIFIER}\\s+)?(?:${alt(MONTHS)}|${alt(SEASONS)}|${alt(HOLIDAYS)})(?:\\s+(?:of\\s+)?20\\d\\d)?\\b`, 'i'),
    // "next spring", "this October", "early 2028"
    new RegExp(`\\b${QUALIFIER}\\s+(?:${alt(MONTHS)}|${alt(SEASONS)}|${alt(HOLIDAYS)}|20\\d\\d|year|month|summer)\\b`, 'i'),
    // "in 6 months", "in a year"
    new RegExp(`\\b${PREPOSITION}\\s+(?:a|\\d{1,2})\\s+(?:months?|years?|weeks?)\\b`, 'i'),
    // "October 2027", "spring 2028" — a year is enough to disambiguate on its own
    new RegExp(`\\b(?:${alt(MONTHS)}|${alt(SEASONS)})\\s+(?:of\\s+)?20\\d\\d\\b`, 'i'),
    // Bare month or holiday. Excluded for AMBIGUOUS words, which need one of the
    // patterns above to prove they are being used as a date.
    new RegExp(`\\b(?:${alt(MONTHS)}|${alt(SEASONS)}|${alt(HOLIDAYS)})\\b`, 'i'),
    // A bare year, last because it carries the least.
    /\b20\d\d\b/,
];
/** Leftover joining words once the time phrase is lifted out of the middle. */
const DANGLING = /^(?:in|on|at|for|to|around|during|and|,|-|–)\s+|\s+(?:in|on|at|for|to|around|during|and|maybe|possibly|probably|ideally|,|-|–)$/gi;
function tidy(fragment) {
    return fragment
        .replace(/\s+/g, ' ')
        .trim()
        .replace(DANGLING, '')
        .replace(/^[,\-–—\s]+|[,\-–—\s]+$/g, '')
        .trim();
}
export function splitTripIntent(input) {
    const text = input.trim();
    if (!text)
        return { destination: '', when: '' };
    for (const pattern of WHEN_PATTERNS) {
        const match = pattern.exec(text);
        if (!match)
            continue;
        const matched = match[0];
        // A bare ambiguous word proves nothing on its own. Only the patterns that
        // carry a preposition, qualifier, or year can claim one, and those all
        // matched more than the word itself.
        const core = matched.toLowerCase().replace(/[^a-z']/g, ' ').trim();
        if (AMBIGUOUS.has(core))
            continue;
        const when = tidy(matched);
        const destination = tidy(text.slice(0, match.index) + ' ' + text.slice(match.index + matched.length));
        // A split that swallows the whole phrase leaves nothing to plan around, so
        // keep it only when the timing was genuinely a part rather than the whole.
        if (!when)
            continue;
        return { destination, when };
    }
    return { destination: text, when: '' };
}
